// Utility functions for P&L calculations
import type { Position, Ticker, ContractInfo } from '../types/mexc';

/**
 * Get contract size for a symbol
 * MEXC USDT-M futures have different contract sizes per symbol
 * @param symbol - Trading symbol
 * @param contractInfo - Optional contract information
 * @returns Contract size
 */
export function getContractSize(symbol: string, contractInfo?: ContractInfo): number {
  // If we have contract info, use it
  if (contractInfo?.contractSize) {
    return contractInfo.contractSize;
  }

  // Known contract sizes for major MEXC USDT-M futures
  // Based on official MEXC API contract details
  const contractSizes: Record<string, number> = {
    'BTC_USDT': 0.0001,
    'ETH_USDT': 0.01,
    'SOL_USDT': 0.1,
    'XRP_USDT': 1,
    'KNC_USDT': 0.1,
    'DOGE_USDT': 1,
    'ADA_USDT': 1,
    'DOT_USDT': 0.1,
    'LINK_USDT': 0.1,
    'LTC_USDT': 0.01,
    'AVAX_USDT': 0.1,
    'ATOM_USDT': 0.1,
    'FIL_USDT': 0.1,
    'TRX_USDT': 1,
    'ETC_USDT': 0.1,
    'CHZ_USDT': 1,
    'SUI_USDT': 1,
    'MX_USDT': 1,
    'BSV_USDT': 0.01,
    'BCH_USDT': 0.01,
    'CRV_USDT': 1,
    'XLM_USDT': 1,
  };

  return contractSizes[symbol] || 0.0001; // Default fallback
}

/**
 * Calculate realized P&L for a position
 * @param position - The position object
 * @returns The realized P&L value
 */
export function calculateRealizedPnL(position: Position): number {
  return position.realised || 0;
}

/**
 * Calculate unrealized P&L for a position using MEXC's official formula
 * MEXC Formula for USDT-M Futures:
 * - Long: (Fair Price - Average Entry Price) × Position Quantity × Size
 * - Short: (Average Entry Price - Fair Price) × Position Quantity × Size
 * @param position - The position object
 * @param currentPrice - Current market price (Fair Price)
 * @param contractInfo - Optional contract information for precise contract size
 * @returns The unrealized P&L value
 */
export function calculateUnrealizedPnL(
  position: Position,
  currentPrice?: number,
  contractInfo?: ContractInfo
): number {
  // If we don't have current price, fall back to realized P&L
  if (!currentPrice) {
    return calculateRealizedPnL(position);
  }

  // Get position data
  const entryPrice = position.openAvgPrice;
  const positionQuantity = position.holdVol; // This is the number of contracts
  const isLong = position.positionType === 1;
  const contractSize = getContractSize(position.symbol, contractInfo);

  if (!entryPrice || !positionQuantity) {
    return calculateRealizedPnL(position);
  }

  // Apply MEXC's official P&L formula for USDT-M Futures
  let pnl: number;
  if (isLong) {
    // Long position: (Fair Price - Average Entry Price) × Position Quantity × Size
    pnl = (currentPrice - entryPrice) * positionQuantity * contractSize;
  } else {
    // Short position: (Average Entry Price - Fair Price) × Position Quantity × Size
    pnl = (entryPrice - currentPrice) * positionQuantity * contractSize;
  }

  return pnl;
}

/**
 * Calculate total P&L (realized + unrealized) for a position
 * @param position - The position object
 * @param currentPrice - Current market price (optional)
 * @param contractInfo - Optional contract information
 * @returns The total P&L value
 */
export function calculateTotalPnL(
  position: Position,
  currentPrice?: number,
  contractInfo?: ContractInfo
): number {
  const realizedPnL = calculateRealizedPnL(position);
  const unrealizedPnL = calculateUnrealizedPnL(position, currentPrice, contractInfo);

  // If we're using current price, return only unrealized (as realized is already included in the calculation)
  // If no current price, return realized P&L
  return currentPrice ? unrealizedPnL : realizedPnL;
}

/**
 * Calculate P&L percentage for a position using MEXC's official formula
 * MEXC Formula: P&L Percentage = (Unrealized P&L / Margin Amount) × 100
 * Where Margin Amount = Average Entry Price × Opening Quantity × Futures Size / Leverage Multiplier
 * @param position - The position object
 * @param currentPrice - Current market price (optional)
 * @param contractInfo - Optional contract information
 * @returns The P&L percentage
 */
export function calculatePnLPercentage(
  position: Position,
  currentPrice?: number,
  contractInfo?: ContractInfo
): number {
  const pnl = calculateTotalPnL(position, currentPrice, contractInfo);
  const contractSize = getContractSize(position.symbol, contractInfo);

  // Calculate margin amount using MEXC's official formula:
  // Margin Amount = Average Entry Price × Opening Quantity × Futures Size / Leverage Multiplier
  const marginAmount = (position.openAvgPrice * position.holdVol * contractSize) / position.leverage;

  return marginAmount > 0 ? (pnl / marginAmount) * 100 : 0;
}

/**
 * Get P&L display data for a position
 * @param position - The position object
 * @param currentPrice - Current market price (optional)
 * @param contractInfo - Optional contract information
 * @returns Object with formatted P&L data
 */
export function getPnLDisplayData(
  position: Position,
  currentPrice?: number,
  contractInfo?: ContractInfo
) {
  const pnl = calculateTotalPnL(position, currentPrice, contractInfo);
  const percentage = calculatePnLPercentage(position, currentPrice, contractInfo);

  return {
    value: pnl,
    percentage,
    isPositive: pnl >= 0,
    formatted: {
      value: pnl.toFixed(2),
      percentage: `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`,
      currency: new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 6,
      }).format(pnl),
    },
  };
}

/**
 * Calculate P&L using ticker data
 * @param position - The position object
 * @param ticker - Ticker data for the symbol
 * @param contractInfo - Optional contract information
 * @returns The P&L value using ticker's last price
 */
export function calculatePnLWithTicker(
  position: Position,
  ticker?: Ticker,
  contractInfo?: ContractInfo
): number {
  if (!ticker) {
    return calculateRealizedPnL(position);
  }

  return calculateUnrealizedPnL(position, ticker.lastPrice, contractInfo);
}

/**
 * Safe P&L calculation with fallbacks
 * @param position - The position object
 * @param tickers - Record of ticker data by symbol
 * @param contractInfo - Optional contract information
 * @returns Safe P&L value with proper fallbacks
 */
export function calculateSafePnL(
  position: Position,
  tickers?: Record<string, Ticker>,
  contractInfo?: ContractInfo
): number {
  try {
    // Try to get current price from tickers
    const ticker = tickers?.[position.symbol];
    if (ticker?.lastPrice) {
      return calculateUnrealizedPnL(position, ticker.lastPrice, contractInfo);
    }

    // Fall back to realized P&L
    return calculateRealizedPnL(position);
  } catch (error) {
    console.warn(`Error calculating P&L for position ${position.symbol}:`, error);
    // Safe fallback
    return position.realised || 0;
  }
}
